import React from 'react';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import { clearStorage, Navigation, Page } from '@bittnerdevelopmentgroup/interactive';
import { deepMap, hydrate, Element, Content } from './DataStructures';
import { BucketComponent, Sortable } from './DragDrop';
import { uniqueId } from './util';
import InteractiveRenderer from './InteractiveRenderer';
import ComponentToolbox from './ComponentToolbox';
import ErrorBoundary from './ErrorBoundary';
import RichText, { textTags } from './controls/RichText';
import CopyComponentButton from './controls/CopyComponentButton';
import { pathReduce, pathAssign, summarizeItem } from './util';
import DestructiveButton from './controls/DestructiveButton';
import Wrapper from './Wrapper';
import './App.css';

// Set to true to debug this file
const DEBUG = false;

// The interactive editor in the center of the app. Can render a page of components, or a text editor with components in it.
export default class InteractiveEditor extends React.Component {
  constructor(props) {
    super(props);
    console.log("InteractiveEditor rootPath", props.rootPathName, props.rootPath);
    this.state = {
      scopedDataPath: props.rootPath,
      selectedChild: undefined,
      selectedChildren: [],
      changesWereMade: false,
      textMode: false,
      copiedComponent: null,
      menuStates: []
    };
    let children = pathReduce(this.props.data, this.state.scopedDataPath);
    if (!children) {
      children = [];
    }
    if (children.children) {
      children = children.children;
    }
    this.setState({
      panelLastIndex: children.length
    });
    this.props.onDropLastIndexChanged(children.length);
    this.editorRef = /*#__PURE__*/React.createRef();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.openInspector = this.openInspector.bind(this);
    this.closeInspector = this.closeInspector.bind(this);
    this.scopeToItem = this.scopeToItem.bind(this);
    this.triggerStateUpdate = this.triggerStateUpdate.bind(this);
    this.onChanged = this.onChanged.bind(this);
    this.setCopiedComponent = this.setCopiedComponent.bind(this);
    this.setMenuStates = this.setMenuStates.bind(this);
  }
  setCopiedComponent(newComponent) {
    this.setState({
      copiedComponent: newComponent
    });
  }
  // Opens the data modal for editing a component.
  openInspector(fullPath) {
    this.props.openInspector(fullPath);
  }
  // Closes the data modal for editing a component.
  closeInspector() {
    this.props.clearInspector();
    this.triggerStateUpdate();
  }
  // Scope the editor into an item, adding to the breadcrumb.
  scopeToItem(fullPath) {
    if (!fullPath.map) {
      fullPath = [fullPath];
    }
    let parent = pathReduce(this.props.data, fullPath.slice(0, -1));
    DEBUG && console.log("breadcrumb scopeToItem called", parent, fullPath);
    if (parent.tagName === undefined) {
      this.props.clearInspector();
    }
    this.setState((state, props) => {
      return {
        scopedDataPath: fullPath
      };
    });
  }
  // Add a child component.
  onComponentListDrop(index, key, didDrop, hydrated) {
    let children = pathReduce(this.props.data, this.state.scopedDataPath);
    if (didDrop) {
      children.splice(index, 1, hydrated);
    } else {
      children.splice(index, 1);
    }
    this.onChanged();
  }
  componentDidMount() {
    document.addEventListener("mousedown", this.onDocumentClick);
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocumentClick);
  }
  // Exit text editing mode when clicking away from the editor.
  onDocumentClick(e) {
    this.setState({
      selectedChild: undefined,
      selectedChildren: [],
      textMode: false,
      selectedParent: undefined,
      selectedKey: undefined
    });
  }
  // Keep parent app informed of our state.
  componentDidUpdate(prevprops, prevstate) {
    if (prevprops.data !== this.props.data && this.state.changesWereMade) {
      this.setState({
        changesWereMade: false
      });
    }
    let children = pathReduce(this.props.data, this.state.scopedDataPath);
    if (!children) {
      children = [];
    }
    if (children.children) {
      children = children.children;
    }
    if (children.length !== prevstate.panelLastIndex) {
      this.setState({
        panelLastIndex: children.length
      });
      this.props.onDropLastIndexChanged(children.length);
    }
  }
  // Change pages
  changePage(i) {
    this.setState((state, props) => {
      props.clearInspector();
      return {
        scopedDataPath: ["panels", i, "children"],
        textMode: false
      };
    });
  }
  onChanged() {
    this.props.onChanged();
  }
  // Open the data modal, but for the Page component instead of a child component.
  openPageConfig(i) {
    this.openInspector(["panels", i]);
  }
  // Ensures that everything rerenders and stays synced up. It's inefficient to keep rerendering things, so TODO: revisit this.
  triggerStateUpdate() {
    DEBUG && console.log("textMode triggerStateUpdate");
    this.props.onUpdate();
    if (this.state.changesWereMade === false) {
      this.setState({
        changesWereMade: true
      });
    }
    //this.forceUpdate()
  }
  // Get an empty index at the end of the current list of children.
  getScopedDataLastIndex() {
    let children = pathReduce(this.props.data, this.state.scopedDataPath);
    DEBUG && console.log("InteractiveEditor getScopedDataLastIndex", children, children.length);
    return children.length;
  }
  setMenuStates(index, active) {
    let newMenuState = Array(this.state.menuStates.length).fill(false);
    newMenuState[index] = active;
    this.setState({
      menuStates: newMenuState
    });
  }

  // Renders the elements of the interactive with wrappers inserted.
  renderEditorComponents(fullPath) {
    ++this.renderChildren_key;
    let children = pathReduce(this.props.data, fullPath);
    if (!children) {
      children = [];
    } else if (!children.map) {
      children = [children];
    }
    if (this.state.menuStates.length === 0 && children.length !== 0) {
      this.setState({
        menuStates: Array(children.length).fill(false)
      });
    }
    if (this.state.textMode) {
      return /*#__PURE__*/React.createElement(RichText, {
        value: children,
        preventUndefined: true,
        onClickNonEditable: () => {
          DEBUG && console.log("InteractiveEditor onClickNonEditable");
          this.setState({
            textMode: false
          });
        },
        onChange: v => {
          DEBUG && console.log("textMode onChange", children);
          pathAssign(this.props.data, fullPath, v);
          this.triggerStateUpdate();
          this.onChanged();
        }
      });
    }
    DEBUG && console.log("InteractiveEditor children", children);
    if (children.length === 0) {
      return /*#__PURE__*/React.createElement(BucketComponent, {
        className: "ib-empty-bucket"
      }, "Drop a Component Here");
    }
    return children.map((child, key) => {
      var _child$props;
      if (child === undefined) {
        return /*#__PURE__*/React.createElement("div", {
          className: "empty-component",
          "data-editor-component": true,
          style: {
            minHeight: '32px'
          }
        });
      }
      let onClick = e => {
        e.stopPropagation();
        if (e.altKey) {
          // alt key prevents default click behavior
          return;
        }
        if (e.shiftKey) {
          let c = this.state.selectedChildren.slice();
          let i = c.indexOf(children[key]);
          if (i === -1) {
            c.push(children[key]);
          } else {
            c.splice(i, 1);
          }
          this.setState({
            selectedChildren: c,
            selectedChild: c.length === 1 ? c[0] : undefined,
            selectedParent: children,
            selectedKey: c.length === 1 ? key : undefined
          });
        } else {
          var _children$key;
          let hasMathJax = false;
          if (children[key].children) {
            for (let i = 0; i < children[key].children.length; i++) {
              var _children$key$childre;
              if (((_children$key$childre = children[key].children[i]) === null || _children$key$childre === void 0 ? void 0 : _children$key$childre.tagName) === 'MathJax') {
                this.openInspector([...fullPath, key, "children", i]);
                hasMathJax = true;
              }
            }
          }
          if ((typeof children[key] === "string" || textTags.includes((_children$key = children[key]) === null || _children$key === void 0 ? void 0 : _children$key.tagName)) && !hasMathJax) {
            if (this.state.selectedChild === children[key]) {
              this.setState({
                textMode: true
              });
              this.closeInspector();
            } else {
              this.setState({
                selectedChild: children[key],
                selectedChildren: [children[key]],
                selectedParent: children,
                selectedKey: key
              });
            }
          } else {
            if (this.state.selectedChild === children[key]) {
              this.openInspector([...fullPath, key]);
            } else {
              this.setState({
                selectedChild: children[key],
                selectedChildren: [children[key]],
                selectedParent: children,
                selectedKey: key
              });
            }
          }
        }
        DEBUG && console.log("onClickCapture!", child);
      };
      if (child.type === "Interactive") {
        return /*#__PURE__*/React.createElement(ErrorBoundary, {
          key: key
        }, /*#__PURE__*/React.createElement(Navigation, {
          onReset: () => {
            DEBUG && console.log("navigation onReset InteractiveEditor");
            clearStorage();
            this.changePage(0);
          }
        }, this.renderEditorComponents([...fullPath, key, "panels"])));
      } else if (child.type === "Panel") {
        return /*#__PURE__*/React.createElement(ErrorBoundary, {
          key: key
        }, /*#__PURE__*/React.createElement(Page, null, this.renderEditorComponents([...fullPath, key, "children"])));
      }
      return /*#__PURE__*/React.createElement(Wrapper, {
        Component: Sortable,
        key: `${fullPath.join("-")}-${key}-${uniqueId(child)}`,
        id: `${fullPath.join("-")}-${key}-${uniqueId(child)}`,
        data: child,
        index: key,
        menuState: this.state.menuStates[key],
        setMenuStates: this.setMenuStates,
        onMove: (fromIndex, toIndex, fromHeight) => {
          let children = pathReduce(this.props.data, this.state.scopedDataPath);
          if (children[fromIndex] === undefined) {
            children[fromIndex] = new Element({
              tagName: 'div',
              props: {
                className: 'dragged-in-placeholder',
                style: {
                  minHeight: fromHeight
                }
              }
            });
          }
          if (fromIndex !== toIndex) {
            const temp = children[fromIndex];
            children.splice(fromIndex, 1);
            children.splice(toIndex, 0, temp);
            this.forceUpdate();
          }
        },
        type: "Component",
        "data-editor-component": true,
        "data-editor-component-selected": this.state.selectedChildren.includes(children[key]) ? true : undefined,
        "data-placeholder": child !== null && child !== void 0 && (_child$props = child.props) !== null && _child$props !== void 0 && _child$props.className ? child.props.className.includes('dragged-in-placeholder') : undefined,
        onMouseUpCapture: onClick
      }, /*#__PURE__*/React.createElement(InteractiveRenderer, {
        data: child,
        storageKey: `children-${uniqueId(child)}-${key}`
      }));
    });
  }
  // Render the editor.
  render() {
    var _pathReduce;
    DEBUG && console.log("InteractiveEditor render");
    this.renderChildren_key = 0;
    return /*#__PURE__*/React.createElement("div", {
      onMouseDown: e => {
        e.stopPropagation();
      }
    }, this.renderEditorComponents(this.state.scopedDataPath), /*#__PURE__*/React.createElement(ButtonToolbar, null, /*#__PURE__*/React.createElement(Button, {
      variant: "success",
      title: "Add selected component",
      className: "mr-2 mt-2",
      disabled: this.props.selectedComponent === undefined,
      onClick: () => {
        let arr = pathReduce(this.props.data, this.state.scopedDataPath);
        let cloned = JSON.parse(JSON.stringify(ComponentToolbox[this.props.selectedComponent]));
        let hydrated = deepMap(cloned, hydrate);
        let i = arr.indexOf(this.state.selectedChild);
        if (i === -1) {
          i = arr.length;
        } else {
          i++;
        }
        DEBUG && console.log("Added new component", i, hydrated);
        arr.splice(i, 0, hydrated);
        this.triggerStateUpdate();
        this.onChanged();
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-plus-lg"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "info",
      title: "Open item properties",
      className: "mr-2 mt-2",
      disabled: this.state.selectedChild === undefined || ((_pathReduce = pathReduce(this.props.data, [...this.state.scopedDataPath, this.state.selectedKey])) === null || _pathReduce === void 0 ? void 0 : _pathReduce.type) !== 'Component',
      onClick: () => {
        this.openInspector([...this.state.scopedDataPath, this.state.selectedKey]);
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-three-dots"
    })), /*#__PURE__*/React.createElement(CopyComponentButton, {
      selectedChildren: this.state.selectedChildren,
      selectedChild: this.state.selectedChild,
      setCopiedComponent: this.setCopiedComponent,
      onChanged: this.onChanged
    }), /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      title: "Paste Component",
      className: "mr-2 mt-2",
      disabled: this.state.copiedComponent === null,
      onClick: () => {
        let arr = pathReduce(this.props.data, this.state.scopedDataPath);
        let i = arr.indexOf(this.state.selectedChild);
        if (i === -1) {
          i = arr.length;
        } else {
          i++;
        }
        let hydrated = deepMap(JSON.parse(this.state.copiedComponent), hydrate);
        hydrated._ibuid = undefined;
        arr.splice(i, 0, hydrated);
        this.triggerStateUpdate();
        this.onChanged();
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-clipboard2-plus"
    })), /*#__PURE__*/React.createElement(DestructiveButton, {
      variant: "danger",
      title: "Delete selected item(s)",
      className: "mr-2 mt-2",
      confirmation: /*#__PURE__*/React.createElement("i", {
        className: "bi bi-question-lg"
      }),
      disabled: this.state.selectedChildren.length === 0,
      text: /*#__PURE__*/React.createElement("i", {
        className: "bi bi-trash"
      }),
      onClick: () => {
        let arr = pathReduce(this.props.data, this.state.scopedDataPath);
        for (const del of this.state.selectedChildren) {
          let i = arr.indexOf(del);
          if (i !== -1) {
            arr.splice(i, 1);
          }
        }
        this.props.clearInspector();
        this.triggerStateUpdate();
        this.onChanged();
      }
    })), /*#__PURE__*/React.createElement("code", {
      style: {
        display: 'none'
      }
    }, JSON.stringify(pathReduce(this.props.data, this.state.scopedDataPath))));
  }
}